import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CalendarDays, Download, FileSearch, Search } from 'lucide-react';
import { api, baixarArquivo, ErroApi } from '../../../api/cliente';
import type {
  EmpresaCaptura,
  NotaRecebida,
  PaginaNotasRecebidas,
  SituacaoNota,
} from '../../../api/tipos';
import {
  Aviso,
  BarraFerramentas,
  Botao,
  BotaoLink,
  Campo,
  Cartao,
  Celula,
  Crescer,
  Esqueleto,
  Etiqueta,
  Paginacao,
  Selecao,
  Tabela,
  Td,
  Th,
  useNotificar,
  Vazio,
} from '../../../componentes/ui';
import { Pagina, TituloPagina } from '../../../componentes/Pagina';
import { chaveLegivel, plural } from '../../../util/formato';
import { formatarDocumento } from '../../../util/mascaras';
import {
  cnpjLegivel,
  dataCurta,
  estadoXml,
  SITUACAO_NOTA,
  valorNota,
} from './status';
import css from './NotasRecebidas.module.css';

const POR_PAGINA = 50;

/** Primeiro dia do mês corrente, `AAAA-MM-DD`. */
function inicioDoMes(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

/**
 * NF-e capturadas da SEFAZ para os CNPJs da carteira. Os filtros vivem na URL:
 * o link "Ver notas" da tela de empresas chega aqui já filtrado, e um F5 não
 * perde o que a pessoa estava olhando.
 */
export function NotasRecebidas() {
  const [params, setParams] = useSearchParams();
  const empresaId = params.get('empresaId') ?? '';
  const de = params.get('de') ?? '';
  const ate = params.get('ate') ?? '';
  const situacao = (params.get('situacao') ?? '') as SituacaoNota | '';
  const pagina = Number(params.get('pagina') ?? 1) || 1;
  const [busca, setBusca] = useState(params.get('busca') ?? '');

  const [empresas, setEmpresas] = useState<EmpresaCaptura[]>([]);
  const [dados, setDados] = useState<PaginaNotasRecebidas | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [baixando, setBaixando] = useState<string | null>(null);
  const notificar = useNotificar();

  const consulta = useMemo(() => {
    const q = new URLSearchParams();
    if (empresaId) q.set('empresaId', empresaId);
    if (de) q.set('de', de);
    if (ate) q.set('ate', ate);
    if (situacao) q.set('situacao', situacao);
    const b = params.get('busca');
    if (b) q.set('busca', b);
    return q;
  }, [empresaId, de, ate, situacao, params]);

  useEffect(() => {
    void api<EmpresaCaptura[]>('/dfe/empresas')
      .then(setEmpresas)
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    let vivo = true;
    setDados(null);
    setErro(null);
    const q = new URLSearchParams(consulta);
    q.set('pagina', String(pagina));
    q.set('porPagina', String(POR_PAGINA));
    api<PaginaNotasRecebidas>(`/dfe/notas?${q.toString()}`)
      .then((d) => vivo && setDados(d))
      .catch(
        (err) =>
          vivo &&
          setErro(err instanceof ErroApi ? err.message : 'Falha ao carregar.'),
      );
    return () => {
      vivo = false;
    };
  }, [consulta, pagina]);

  function mudar(campo: string, valor: string) {
    const p = new URLSearchParams(params);
    if (valor) p.set(campo, valor);
    else p.delete(campo);
    p.delete('pagina');
    setParams(p, { replace: true });
  }

  async function baixar(n: NotaRecebida) {
    setBaixando(n.id);
    try {
      await baixarArquivo(`/dfe/notas/${n.id}/xml`, `${n.chave}.xml`);
    } catch (err) {
      notificar(
        err instanceof ErroApi ? err.message : 'Não foi possível baixar o XML.',
        'erro',
      );
    } finally {
      setBaixando(null);
    }
  }

  async function baixarZip() {
    setBaixando('zip');
    try {
      const sufixo = [de, ate].filter(Boolean).join('_a_');
      await baixarArquivo(
        `/dfe/notas/zip?${consulta.toString()}`,
        `nfe-recebidas${sufixo ? `-${sufixo}` : ''}.zip`,
      );
    } catch (err) {
      notificar(
        err instanceof ErroApi ? err.message : 'Não foi possível gerar o ZIP.',
        'erro',
      );
    } finally {
      setBaixando(null);
    }
  }

  const filtrando = !!(
    empresaId ||
    de ||
    ate ||
    situacao ||
    params.get('busca')
  );

  return (
    <Pagina largura="larga">
      <TituloPagina
        titulo="Notas recebidas"
        descricao="NF-e emitidas contra os CNPJs da sua carteira, capturadas direto da SEFAZ."
        acoes={
          <Botao
            icone={<Download />}
            onClick={() => void baixarZip()}
            carregando={baixando === 'zip'}
            disabled={!dados?.total}
          >
            Baixar ZIP do filtro
          </Botao>
        }
      />

      {erro && <Aviso>{erro}</Aviso>}

      <Cartao padding={0}>
        <BarraFerramentas>
          <Selecao
            rotulo="Empresa"
            value={empresaId}
            onChange={(e) => mudar('empresaId', e.target.value)}
          >
            <option value="">Todas</option>
            {empresas.map((e) => (
              <option key={e.id} value={e.id}>
                {e.nome} · {cnpjLegivel(e.cnpj)}
              </option>
            ))}
          </Selecao>
          <Campo
            rotulo="Emitidas de"
            type="date"
            value={de}
            onChange={(e) => mudar('de', e.target.value)}
          />
          <Campo
            rotulo="Até"
            type="date"
            value={ate}
            onChange={(e) => mudar('ate', e.target.value)}
          />
          <Selecao
            rotulo="Situação"
            value={situacao}
            onChange={(e) => mudar('situacao', e.target.value)}
          >
            <option value="">Todas</option>
            <option value="AUTORIZADA">Autorizadas</option>
            <option value="CANCELADA">Canceladas</option>
            <option value="DENEGADA">Denegadas</option>
          </Selecao>
          <Crescer>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                mudar('busca', busca.trim());
              }}
            >
              <Campo
                rotulo="Fornecedor, CNPJ ou chave"
                icone={<Search />}
                type="search"
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder="Buscar e Enter"
              />
            </form>
          </Crescer>
          <Botao
            aparencia="secundario"
            icone={<CalendarDays />}
            onClick={() => mudar('de', inicioDoMes())}
            title="Notas emitidas desde o dia 1º deste mês"
          >
            Este mês
          </Botao>
        </BarraFerramentas>

        {dados === null ? (
          <div className={css.carregando}>
            <Esqueleto altura={20} />
            <Esqueleto altura={20} />
            <Esqueleto altura={20} />
            <Esqueleto altura={20} />
          </div>
        ) : dados.total === 0 ? (
          <Vazio
            icone={<FileSearch />}
            titulo={
              filtrando
                ? 'Nenhuma nota com estes filtros'
                : 'Nenhuma nota recebida ainda'
            }
            descricao={
              empresas.length === 0
                ? 'Adicione uma empresa à captura automática para as notas começarem a chegar.'
                : 'As notas aparecem aqui conforme a SEFAZ as disponibiliza — a consulta roda de hora em hora.'
            }
            acao={
              empresas.length === 0 && (
                <BotaoLink to="/painel/empresas">
                  Ir para a captura automática
                </BotaoLink>
              )
            }
          />
        ) : (
          <>
            <div className={css.resumo}>
              <strong className="num">
                {plural(dados.total, 'nota', 'notas')}
              </strong>
              <span>
                Valor total{' '}
                <strong className="num">{valorNota(dados.valorTotal)}</strong>
              </span>
            </div>
            <Tabela rotulo="Notas recebidas">
              <thead>
                <tr>
                  <Th>Emissão</Th>
                  <Th>Fornecedor</Th>
                  <Th>Destinatário</Th>
                  <Th alinhar="direita">Valor</Th>
                  <Th>Situação</Th>
                  <Th>XML</Th>
                  <Th encolher>
                    <span className="sr-only">Ações</span>
                  </Th>
                </tr>
              </thead>
              <tbody>
                {dados.itens.map((n) => {
                  const xml = estadoXml(n);
                  const sit = SITUACAO_NOTA[n.situacao];
                  return (
                    <tr key={n.id}>
                      <Td>
                        <span className={css.emissao}>
                          {dataCurta(n.dataEmissao)}
                        </span>
                      </Td>
                      <Td>
                        <Celula
                          principal={n.emitenteNome}
                          secundaria={
                            <>
                              {formatarDocumento(n.emitenteDoc)}
                              <span
                                className={css.chave}
                                title="Chave de acesso"
                              >
                                {chaveLegivel(n.chave)}
                              </span>
                            </>
                          }
                        />
                      </Td>
                      <Td>
                        <span className={css.destinatario}>
                          {n.empresa.nome}
                        </span>
                      </Td>
                      <Td alinhar="direita">
                        <span className={css.valor}>{valorNota(n.valor)}</span>
                      </Td>
                      <Td>
                        <Etiqueta tom={sit.tom}>{sit.texto}</Etiqueta>
                      </Td>
                      <Td>
                        <Etiqueta tom={xml.tom} title={xml.dica}>
                          {xml.texto}
                        </Etiqueta>
                      </Td>
                      <Td encolher>
                        {n.disponivel ? (
                          <Botao
                            pequeno
                            aparencia="secundario"
                            icone={<Download />}
                            carregando={baixando === n.id}
                            onClick={() => void baixar(n)}
                          >
                            XML
                          </Botao>
                        ) : null}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </Tabela>
            <Paginacao
              total={dados.total}
              pagina={pagina}
              porPagina={POR_PAGINA}
              rotulo={dados.total === 1 ? 'nota' : 'notas'}
              aoMudar={(p) => {
                const q = new URLSearchParams(params);
                q.set('pagina', String(p));
                setParams(q);
              }}
            />
          </>
        )}
      </Cartao>
    </Pagina>
  );
}
